import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { participantService, JoinInfo } from '../../services/participant.service';

export default function ParticipantJoin() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [joinInfo, setJoinInfo] = useState<JoinInfo | null>(null);
  const [name, setName] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    participantService.getJoinInfo(token)
      .then(setJoinInfo)
      .catch(err => setLoadError(err.message));
  }, [token]);

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !name.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await participantService.join(token, name.trim());
      navigate(`/p/${token}/session`, { state: { sessionId: result.sessionId, name: result.name } });
    } catch (err: any) {
      setSubmitError(err.body?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <div className="fixed inset-0 bg-navy flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-white/40 text-5xl mb-4">✗</div>
          <p className="text-white font-semibold text-lg mb-2">Invalid or expired link</p>
          <p className="text-white/60 text-sm">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!joinInfo) {
    return (
      <div className="fixed inset-0 bg-navy flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-navy overflow-y-auto flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="w-12 h-12 bg-primary rounded-xl mx-auto mb-4 flex items-center justify-center">
            <span className="text-navy font-bold text-xl">S</span>
          </div>
          <p className="text-white/60 text-sm uppercase tracking-widest mb-1">You're invited to</p>
          <h1 className="text-white font-bold text-2xl leading-tight">{joinInfo.sessionName}</h1>
        </div>

        <form onSubmit={handleJoin} className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">Your name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter your name"
              autoFocus
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-white placeholder-white/30 text-base focus:outline-none focus:border-primary focus:bg-white/15 transition-all"
              maxLength={80}
            />
          </div>

          {submitError && (
            <p className="text-red-400 text-sm bg-red-400/10 rounded-lg px-3 py-2">{submitError}</p>
          )}

          <button
            type="submit"
            disabled={!name.trim() || submitting}
            className="w-full bg-primary text-navy font-semibold py-3.5 rounded-xl text-base disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all"
          >
            {submitting ? 'Joining…' : 'Join session'}
          </button>

          <p className="text-white/40 text-xs text-center leading-relaxed">
            Your conversations with avatars in this session may be reviewed by
            your facilitator afterwards.
          </p>
        </form>
      </div>
    </div>
  );
}
